import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { DialogDeleteSiteComponent } from '@app/modules/direct/dialogs/dialog-delete-site/dialog-delete-site.component';
import { DialogPublishSiteComponent } from '@app/modules/direct/dialogs/dialog-publish-site/dialog-publish-site.component';
import { DialogRenameSiteComponent } from '@app/modules/direct/dialogs/dialog-rename-site/dialog-rename-site.component';
import { CustomSitesService } from '@app/modules/direct/services/custom-sites.service';
import { DirectSite, NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { combineLatest, map } from 'rxjs';
import { DirectService, DirectStatus } from './direct.service';

export type DirectStatusStepId =
  | 'create-site'
  | 'add-payout'
  | 'publish-site'
  | 'taxes'
  | 'activate-direct'
  | 'no-properties';

export interface DirectStatusStep {
  id: DirectStatusStepId;
  label?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DirectDashboardService {
  status: DirectStatus;
  steps: DirectStatusStep[] = [];
  showSites: boolean;
  loadingSites: boolean;
  sites: DirectSite[];

  constructor(
    private direct: DirectService,
    private customSitesService: CustomSitesService,
    private router: Router,
    private dialog: MatDialog,
    private toast: ToastNotificationsService
  ) {}

  getDirectStatus() {
    this.direct.getDirectStatus().subscribe((status) => {
      this.status = status;
      this.steps = [];

      const shouldGuideSelfHosted = status.hasSelfHostedSites && !status.hasDirectSites;
      if (shouldGuideSelfHosted) {
        this.getSites();

        if (!status.hasPaymentMethods) {
          this.addStep('add-payout');
        }

        if (status.shouldSelectProperties) {
          this.addStep('no-properties');
        }
      } else {
        if (!status.hasDirectSites) {
          this.addStep('create-site');
        } else {
          this.getSites();
        }

        if (!status.hasPaymentMethods) {
          this.addStep('add-payout');
        }

        if (!status.taxesConfigured) {
          this.addStep('taxes');
        }

        if (!status.isDirectActive) {
          this.addStep('activate-direct');
        }

        if (!status.hasPublishedSites) {
          this.addStep('publish-site');
        }
      }
    });
  }

  destroy() {
    this.status = null;
    this.steps = [];
    this.showSites = false;
    this.loadingSites = false;
    this.sites = [];
  }

  getSites() {
    this.showSites = true;
    this.loadingSites = true;

    combineLatest([this.direct.getSites(), this.customSitesService.getCustomSites()])
      .pipe(map(([sites, customSites]) => [...sites, ...customSites]))
      .subscribe((sites) => {
        this.sites = sites;
        this.loadingSites = false;
      });
  }

  addSite() {
    this.router.navigate(['/direct/sites/new']);
  }

  renameSite(id: string) {
    const siteIndex = this.sites.findIndex((site) => site.id === id);

    const site = this.sites[siteIndex];

    const dialogRef = this.dialog.open(DialogRenameSiteComponent, {
      width: '720px',
      data: site,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.sites[siteIndex].name = result.name;
    });
  }

  deleteSite(id: string) {
    const siteIndex = this.sites.findIndex((site) => site.id === id);
    const site = this.sites[siteIndex];

    const dialogRef = this.dialog.open(DialogDeleteSiteComponent, {
      width: '720px',
      data: site,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sites.splice(siteIndex, 1);
      }
    });
  }

  publishSite() {
    const unpublishedSites = this.sites.filter((site) => site.status !== 'Live' && site.type === 'Hospitable hosted');

    if (!unpublishedSites.length) {
      this.toast.open('There are no sites to publish', 'Dismiss', NotificationType.Error);
      this.getDirectStatus();
      return;
    }

    if (unpublishedSites.length === 1) {
      this.direct.publishSite(unpublishedSites[0].id).subscribe(() => {
        this.getDirectStatus();
      });
    } else {
      const dialogRef = this.dialog.open(DialogPublishSiteComponent, {
        width: '720px',
        data: unpublishedSites[0],
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) this.getDirectStatus();
      });
    }
  }

  selectProperties() {
    const siteWithNoProperties = this.sites.find((site) => site.properties.length === 0 && site.type === 'Self hosted');

    if (!siteWithNoProperties) {
      this.toast.open('There are no sites that can support properties', 'Dismiss', NotificationType.Error);
      this.getDirectStatus();
      return;
    }

    this.router.navigate([`/direct/custom-sites/${siteWithNoProperties.id}`]);
  }

  private addStep(id: DirectStatusStepId) {
    this.steps.push({ id, label: this.mapLabel(id) });
  }

  private mapLabel(id: DirectStatusStepId) {
    switch (id) {
      case 'create-site':
        return 'Create a site';
      case 'add-payout':
        return 'Add a payout method';
      case 'no-properties':
        return 'Select properties';
      case 'publish-site':
        return 'Publish your site';
      case 'activate-direct':
        return 'Activate Direct';
      case 'taxes':
        return 'Set up Taxes';
    }
  }
}
